/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/ui/Button.tsx
 *
 * Rôle :
 *   Bouton primitif réutilisable, conforme à la palette TNE Vosges et au
 *   wording français-France strict (§15.5). Trois variantes (primary,
 *   secondary, tertiary) + trois tailles (sm, md, lg) + état loading.
 *
 *   Accessibilité (WCAG 2.1 AA — §8.2.5, §14.6.NF2) :
 *     - Focus visible (outline rouge corail à 3 px, défini en CSS).
 *     - `aria-busy` géré quand `loading=true`.
 *     - Désactivation clavier conforme (`disabled` + tabindex auto).
 *     - Tap target ≥ 44 px en taille `md` et `lg` (mobile friendly).
 *
 * Réutilisable :
 *   100 %. Portable directement vers NOLAS modernisé en remplaçant
 *   `src/styles/tokens.css` par les tokens NOLAS. Aucune dépendance au
 *   métier TNE — n'importe quel projet React DRANE peut le copier.
 *
 * Voir : CLAUDE.md §15 (composants standardisés), §17.5 (checklist
 *        réutilisabilité), §8.2 (PWA + accessibilité).
 *
 * @author Mika
 * @since 2026-05-04
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

/** Variantes visuelles (cf. CLAUDE.md §15 « Composants à standardiser »). */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger';

/** Tailles. `md` est le défaut, recommandé pour mobile (≥ 44 px). */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Variante visuelle. */
  variant?: ButtonVariant;
  /** Taille du bouton. */
  size?: ButtonSize;
  /** Affiche un spinner et désactive l'interaction. */
  loading?: boolean;
  /** Étend la largeur du bouton à 100 % du parent. */
  fullWidth?: boolean;
  /** Icône optionnelle placée avant le label (ex. <FontAwesomeIcon …/>). */
  iconStart?: ReactNode;
  /** Icône optionnelle placée après le label. */
  iconEnd?: ReactNode;
  /** `type` HTML — défaut `button` (jamais `submit` par accident). */
  type?: 'button' | 'submit' | 'reset';
}

/**
 * Bouton standardisé TNE 2026.
 *
 * @example
 *   <Button variant="primary" onClick={handleSignup}>
 *     <FormattedMessage id="btn.signup" />
 *   </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    iconStart,
    iconEnd,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  const composedClassName = [
    styles.button,
    styles[`variant_${variant}`],
    styles[`size_${size}`],
    fullWidth ? styles.fullWidth : '',
    loading ? styles.loading : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={composedClassName}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {iconStart && <span className={styles.iconStart}>{iconStart}</span>}
      <span className={styles.label}>{children}</span>
      {iconEnd && <span className={styles.iconEnd}>{iconEnd}</span>}
      {loading && (
        <span className={styles.spinner} aria-hidden="true">
          {/* Spinner CSS pur — pas de dépendance externe. */}
        </span>
      )}
    </button>
  );
});

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Ajouter une prop `as` polymorphique pour rendre <a> ou <Link> sans
//    casser l'API actuelle (utile pour les "boutons" qui sont en réalité
//    des liens — meilleure accessibilité).
//  - Ajouter une variante `success` pour confirmations post-action.
// Notes pour rétro-port NOLAS :
//  - Remplace les <button class="btn btn-primary"> de NOLAS Tracking par
//    <Button variant="primary">. Les classes Materialize peuvent être
//    drop-in remplacées par ces variantes.
// ──────────────────────────────────────────────────────────────────────────
